import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import { violationKeys } from "./use-violations";

type WorkAssignment = components["schemas"]["WorkAssignment"];
type WorkAssignmentListResponse = components["schemas"]["WorkAssignmentListResponse"];
type WorkAssignmentCreate = components["schemas"]["WorkAssignmentCreate"];
type WorkAssignmentUpdate = components["schemas"]["WorkAssignmentUpdate"];
type WorkAssignmentStatus = components["schemas"]["WorkAssignmentStatus"];
type WorkAssignmentOrigin = components["schemas"]["WorkAssignmentOrigin"];
type FormTargetKind = components["schemas"]["FormTargetKind"];
type WorkAssignmentConflictError = components["schemas"]["WorkAssignmentConflictError"];
type GenerateWorkAssignmentsResponse = components["schemas"]["GenerateWorkAssignmentsResponse"];
type ViolationPromotionRequest = components["schemas"]["ViolationPromotionRequest"];

// Cache windows follow the field-credentials canon: short list staleness so
// fresh writes appear quickly, with keepPreviousData smoothing pagination.
const LIST_STALE_TIME = 60_000;
const LIST_GC_TIME = 5 * 60_000;
const DETAIL_STALE_TIME = 2 * 60_000;
const DETAIL_GC_TIME = 10 * 60_000;

/** Mirrors the listWorkAssignments OpenAPI sort_by enum (the Go ORDER BY allow-list). */
export type WorkAssignmentSortBy = "due_date" | "status" | "created_at";

/**
 * True for the three open, not-yet-submitted statuses on the monotonic
 * forward-only lifecycle (scheduled → assigned → in_progress → submitted →
 * completed; cancelled from any non-terminal status). Gates the "Complete
 * test" entry point into the one-off composer: a `submitted` row already has
 * a submission of record, and `completed`/`cancelled` are terminal, so none
 * of the three should offer a second composer entry.
 */
export function isAssignmentComposable(status: WorkAssignmentStatus): boolean {
  return status === "scheduled" || status === "assigned" || status === "in_progress";
}

export interface WorkAssignmentsListParams {
  /** Assignee profile id. */
  assigned_to?: string;
  /**
   * Assignee-kind facet (the server's listWorkAssignments `assignee_kind`
   * enum): `tester` = a person is assigned, `company_pool` = bound to a
   * company but not yet claimed by a tester, `unassigned` = no assignee.
   */
  assignee_kind?: "tester" | "company_pool" | "unassigned";
  status?: WorkAssignmentStatus;
  /** Live predicate: due_date + policy grace < today on an open status. */
  overdue?: boolean;
  /** Row provenance (`origin=follow_up` narrows to open follow-up work). */
  origin?: WorkAssignmentOrigin;
  /** Restrict to OPEN assignments (status NOT IN completed/cancelled). */
  open?: boolean;
  /** Inclusive lower bound on completion time (updated_at proxy). */
  completed_from?: string;
  /** Exclusive upper bound on completion time (updated_at proxy). */
  completed_to?: string;
  facility_id?: string;
  device_installation_id?: string;
  template_id?: string;
  /** The enforcement case the work belongs to. */
  violation_id?: string;
  /** Narrow to device-test or premise-inspection assignments. */
  target_kind?: FormTargetKind;
  limit?: number;
  offset?: number;
  sort_by?: WorkAssignmentSortBy;
  sort_dir?: "asc" | "desc";
}

/**
 * Query-key factory for org-side work-assignment caches. Org-param routes
 * thread orgId through EVERY key including detail — a detail key without
 * orgId would serve stale cross-org rows after an org switch.
 */
export const workAssignmentKeys = {
  all: ["work-assignments"] as const,
  lists: () => [...workAssignmentKeys.all, "list"] as const,
  list: (orgId: string, params: WorkAssignmentsListParams) =>
    [...workAssignmentKeys.lists(), orgId, params] as const,
  detail: (orgId: string, id: string) => [...workAssignmentKeys.all, "detail", orgId, id] as const,
} as const;

function withDefaults(params: WorkAssignmentsListParams): WorkAssignmentsListParams {
  return {
    ...params,
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
  };
}

/** Paginated org work-assignment list (cadence / schedule / manual origins).
 * `enabled: false` holds the request — a caller without `work_assignments:read`
 * never issues it. */
export function useWorkAssignments(
  params: WorkAssignmentsListParams = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const orgId = useAppStore((s) => s.activeOrgId);
  const merged = withDefaults(params);
  return useQuery<WorkAssignmentListResponse>({
    queryKey: workAssignmentKeys.list(orgId ?? "", merged),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/work-assignments", {
        params: { path: { orgId }, query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list work assignments";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: enabled && Boolean(orgId),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/** One work assignment. */
export function useWorkAssignment(id: string | null | undefined) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<WorkAssignment>({
    queryKey: workAssignmentKeys.detail(orgId ?? "", id ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      if (!id) throw new Error("Assignment id is required");
      const { data, error, response } = await apiClient.GET(
        "/orgs/{orgId}/work-assignments/{assignmentId}",
        {
          params: { path: { orgId, assignmentId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId) && Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

/**
 * Create a manual assignment (org_admin any-assignee, or a member's
 * unscheduled self-create). The server pins the template's published version and
 * derives target_kind. The 422 `invalid_assignee` /
 * `template_not_published` envelopes are threaded through `ApiError` (code +
 * details) so the form renders them inline against the offending field.
 */
export function useCreateWorkAssignment() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: WorkAssignmentCreate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST("/orgs/{orgId}/work-assignments", {
        params: { path: { orgId } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * PATCH due_date / assigned_to (three-state) / status (forward-only). The
 * 409 FSM envelope (`assignment_invalid_transition` / `assignment_terminal`)
 * is threaded through `ApiError.details`.
 */
export function useUpdateWorkAssignment(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: WorkAssignmentUpdate) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.PATCH(
        "/orgs/{orgId}/work-assignments/{assignmentId}",
        {
          params: { path: { orgId, assignmentId: id } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.detail(orgId, id) });
      }
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * Cancel an assignment (any non-terminal status → cancelled). Cancellation
 * is the revocation flow — it severs the assigned tester's target-hydration
 * visibility. The 409 terminal envelope is threaded through
 * `ApiError.details`.
 */
export function useCancelWorkAssignment(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/work-assignments/{assignmentId}/cancel",
        {
          params: { path: { orgId, assignmentId: id } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to cancel work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.detail(orgId, id) });
      }
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * Cancel a work assignment by id supplied AT CALL TIME (not bound to the
 * hook call site) — the call-time-id mutation shape (mirrors
 * `useDetachInstallation`/`useReplaceInstallation`), needed by
 * `WalkupWizard`'s cancel-on-abandon flow: the wizard cancels whichever
 * prior UNSUBMITTED assignment it is abandoning on a device change, a
 * DIFFERENT id every time, which `useCancelWorkAssignment(id)`'s
 * hook-render-time-bound id can't express. Same cancel verb + invalidation
 * set as `useCancelWorkAssignment`.
 */
export function useCancelWorkAssignmentById() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/work-assignments/{assignmentId}/cancel",
        {
          params: { path: { orgId, assignmentId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to cancel work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (_data, assignmentId) => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: workAssignmentKeys.detail(orgId, assignmentId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * Claim a device's unassigned open test for the caller — the staff claim the
 * walk-up wizard runs before it binds the row. The id is supplied at call time
 * (the wizard claims whichever row its open-row read found). The 200 body is
 * the row assigned to the caller in `assigned`; a row that is no longer
 * claimable answers the 409 `assignment_not_claimable` envelope, threaded
 * through `ApiError` (code + details). Invalidates the row's detail and every
 * list, the device's open-row read among them.
 */
export function useClaimWorkAssignment() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (assignmentId: string): Promise<WorkAssignment> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/work-assignments/{assignmentId}/claim",
        {
          params: { path: { orgId, assignmentId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to claim work assignment";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: (_data, assignmentId) => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: workAssignmentKeys.detail(orgId, assignmentId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * Run the device-test cadence sweep (org_admin). Returns the run report
 * (created / skipped / policies without a published version) for the
 * result toast; new rows land in the list caches.
 */
export function useGenerateWorkAssignments() {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (): Promise<GenerateWorkAssignmentsResponse> => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/work-assignments/generate",
        {
          params: { path: { orgId } },
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to generate work assignments";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.lists() });
    },
  });
}

/**
 * Promote an overdue assignment to a violation (org_admin manual promotion,
 * never auto-create). The body carries the correction due date and, for every
 * kind but a device test, the condition type the coordinator names (a device
 * test's type is fixed by the verb). Invalidates the assignment detail (the
 * 201 body carries the linked violation_id) and, cross-entity, the violations
 * list: the verb opens a case (origin `promoted_work_assignment`) that must
 * appear on the violations surface without a manual refresh. The 409
 * one-violation-guard / typed 422 envelopes are threaded through
 * `ApiError.details` with the server's `code` on `ApiError.code`.
 */
export function useCreateViolationFromAssignment(id: string) {
  const queryClient = useQueryClient();
  const orgId = useAppStore((s) => s.activeOrgId);
  return useMutation({
    mutationFn: async (body: ViolationPromotionRequest) => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/work-assignments/{assignmentId}/create-violation",
        {
          params: { path: { orgId, assignmentId: id } },
          body,
        },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to create violation";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: workAssignmentKeys.detail(orgId, id) });
      }
      void queryClient.invalidateQueries({ queryKey: violationKeys.lists() });
    },
  });
}

export { LIST_STALE_TIME, LIST_GC_TIME, DETAIL_STALE_TIME, DETAIL_GC_TIME };

export type {
  WorkAssignment,
  WorkAssignmentListResponse,
  WorkAssignmentCreate,
  WorkAssignmentUpdate,
  WorkAssignmentStatus,
  WorkAssignmentOrigin,
  WorkAssignmentConflictError,
  GenerateWorkAssignmentsResponse,
  ViolationPromotionRequest,
};
